import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import {
    getNewAddedItems,
    getSelectedCatalogItems,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import {
    type CustomListItem,
    getCustomListCatalogItem,
    getCustomListCatalogItemId,
} from 'entities/CustomList';
import { AddCatalogItemWithQty } from 'features/AddCatalogItemWithQty';
import {
    addItemsToOrderService,
    buildSingleAddItemData,
} from 'features/AddItemsToOrder';
import { addItemsToOrderActions } from 'features/AddItemsToOrder/model/slices/addItemsToOrderSlice';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';
import { useCatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

interface OrderListItemAddCellProps {
    listItem: CustomListItem;
    listId: string;
}

export const OrderListItemAddCell = memo(
    ({ listItem, listId }: OrderListItemAddCellProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const location = useLocation();
        const identity = useCatalogIdentity();
        const selectedItems = useSelector(getSelectedCatalogItems);
        const newAddedItems = useSelector(getNewAddedItems);
        const selectedOrderId = location.state?.selectedOrderId as
            | string
            | undefined;

        const catalogItem = getCustomListCatalogItem(listItem);
        const catalogItemId = getCustomListCatalogItemId(listItem);
        const targetQuantity = listItem.targetQuantity || 1;
        const selectionKey = catalogItem
            ? getItemUid(catalogItem, identity)
            : listItem.id;

        const selection = selectedItems?.[selectionKey];
        const qty = selection?.qty ?? 0;
        const orderItemId = selection?.orderItemId;
        const isActive = Boolean(selection && qty > 0);
        const [loading, setLoading] = useState(false);

        const onAdd = useCallback(async () => {
            if (!catalogItem || !catalogItemId) {
                return;
            }

            setLoading(true);
            try {
                const existingQty = selection?.qty ?? 0;
                const nextQty = existingQty > 0 ? existingQty : targetQuantity;
                const existingNewItem = newAddedItems?.[selectionKey];

                const commitSelection = () => {
                    dispatch(
                        catalogItemActions.addNewItem({
                            itemId: selectionKey,
                            source: existingNewItem?.source ?? 'lists',
                            categoryId:
                                existingNewItem?.categoryId ??
                                catalogItem.categoryId,
                            listId,
                        }),
                    );

                    if (!selection) {
                        dispatch(
                            catalogItemActions.addMultiselectItem({
                                id: selectionKey,
                                uid: selectionKey,
                                catalogItemId,
                                title: catalogItem.title,
                                propertyCategoryId: catalogItem.categoryId,
                            }),
                        );
                    }

                    dispatch(
                        catalogItemActions.setItemQuantity({
                            id: selectionKey,
                            value: nextQty,
                        }),
                    );
                };

                if (selectedOrderId && orderItemId) {
                    const result = await dispatch(
                        editOrderItem({
                            orderId: selectedOrderId,
                            itemId: orderItemId,
                            qty: nextQty,
                        }),
                    );
                    if (result.meta.requestStatus !== 'fulfilled') {
                        toast.error(t('Something went wrong'));
                        return;
                    }
                    commitSelection();
                    return;
                }

                if (selectedOrderId) {
                    dispatch(
                        addItemsToOrderActions.setIsLoadingAddItemsToOrder(
                            true,
                        ),
                    );
                    try {
                        const result = await dispatch(
                            addItemsToOrderService({
                                orderId: selectedOrderId,
                                data: buildSingleAddItemData('catalog', {
                                    itemId: catalogItemId,
                                    qty: nextQty,
                                }),
                            }),
                        );
                        if (result.meta.requestStatus !== 'fulfilled') {
                            toast.error(t('Something went wrong'));
                            return;
                        }
                    } finally {
                        dispatch(
                            addItemsToOrderActions.setIsLoadingAddItemsToOrder(
                                false,
                            ),
                        );
                    }
                }

                commitSelection();
            } finally {
                setLoading(false);
            }
        }, [
            catalogItem,
            catalogItemId,
            dispatch,
            listId,
            newAddedItems,
            orderItemId,
            selectedOrderId,
            selection,
            selectionKey,
            t,
            targetQuantity,
        ]);

        const onSetQty = useCallback(
            async (nextQty: number) => {
                setLoading(true);
                try {
                    if (nextQty < 1) {
                        if (selectedOrderId && orderItemId) {
                            const result = await dispatch(
                                deleteOrderItem({
                                    orderId: selectedOrderId,
                                    itemId: orderItemId,
                                }),
                            );
                            if (result.meta.requestStatus !== 'fulfilled') {
                                toast.error(t('Something went wrong'));
                                return;
                            }
                        }
                        dispatch(
                            catalogItemActions.removeMultiselectItem(
                                selectionKey,
                            ),
                        );
                        dispatch(
                            catalogItemActions.removeNewItem(selectionKey),
                        );
                        return;
                    }

                    if (selectedOrderId && orderItemId) {
                        const result = await dispatch(
                            editOrderItem({
                                orderId: selectedOrderId,
                                itemId: orderItemId,
                                qty: nextQty,
                            }),
                        );
                        if (result.meta.requestStatus !== 'fulfilled') {
                            toast.error(t('Something went wrong'));
                            return;
                        }
                    }

                    dispatch(
                        catalogItemActions.setItemQuantity({
                            id: selectionKey,
                            value: nextQty,
                        }),
                    );
                } finally {
                    setLoading(false);
                }
            },
            [dispatch, orderItemId, selectedOrderId, selectionKey, t],
        );

        return (
            <AddCatalogItemWithQty
                active={isActive}
                value={isActive ? qty : targetQuantity}
                loading={loading}
                onSetValue={onSetQty}
                onItemToggleSelect={onAdd}
            />
        );
    },
);

OrderListItemAddCell.displayName = 'OrderListItemAddCell';
